import {
  ActivatedRouteSnapshot,
  DetachedRouteHandle,
  RouteReuseStrategy,
} from "@angular/router";

/**
 *
 * Memorizza i percorsi Angular scollegati e determina quando possono essere ripristinati o riutilizzati.
 */
export class RouteReusePath implements RouteReuseStrategy {
  public handlers: { [key: string]: DetachedRouteHandle } = {};

  /**
   *
   * Determina se il router deve scollegare e memorizzare il percorso corrente.
   * @param route Istantanea o dati del percorso usati dal router.
   * @returns `true` se il percorso deve essere scollegato.
   */
  public shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return route.data.shouldReuse ?? false;
  }

  /**
   *
   * Memorizza il riferimento a un percorso scollegato per una navigazione successiva.
   * @param route Istantanea o dati del percorso usati dal router.
   * @param handle Riferimento al percorso scollegato da memorizzare o ripristinare.
   */
  public store(
    route: ActivatedRouteSnapshot,
    handle: DetachedRouteHandle,
  ): void {
    if (route.data.shouldReuse ?? false) {
      this.handlers[route.routeConfig.path] = handle;
    }
  }

  /**
   *
   * Determina se un percorso memorizzato può essere collegato al percorso richiesto.
   * @param route Istantanea o dati del percorso usati dal router.
   * @returns `true` se il percorso può essere collegato.
   */
  public shouldAttach(route: ActivatedRouteSnapshot): boolean {
    return !!route.routeConfig && !!this.handlers[route.routeConfig.path];
  }

  /**
   *
   * Restituisce e rimuove, se disponibile, il riferimento memorizzato per il percorso richiesto.
   * @param route Istantanea o dati del percorso usati dal router.
   * @returns Riferimento al percorso memorizzato, se disponibile.
   */
  public retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle {
    if (!route.routeConfig) {
      return null;
    }

    return this.handlers[route.routeConfig.path];
  }

  /**
   *
   * Determina se Angular deve riutilizzare il percorso attivo per la navigazione successiva.
   * @param future Percorso previsto per la navigazione successiva.
   * @param curr Percorso attualmente attivo.
   * @returns `true` se il percorso attivo deve essere riutilizzato.
   */
  public shouldReuseRoute(
    future: ActivatedRouteSnapshot,
    curr: ActivatedRouteSnapshot,
  ): boolean {
    return future.data.shouldReuse ?? false;
  }
}
